<template>
    <span
        class="tooltip"
        :class="[{ 'm-right': right, 'm-left': left, multiline, open: isOpen }, $attrs.class]"
        @click="onClick"
        @pointerenter="onHoverStart"
        @pointerleave="onHoverEnd"
    >
        <slot />
        <Popup
            type="tooltip"
            :open="isOpen"
            :hover="isHovering"
            :position="position"
            :align="align"
            :teleport="teleport"
            :dark="dark"
            :translucent="translucent"
            :role="presentation ? 'presentation' : 'tooltip'"
            :focus-trap="false"
            :always-open="alwaysOpen"
            :prevent-outside-click="!click"
            v-bind="$attrs"
            @open="open"
            @close="close"
            @toggle="$emit('toggle', $event)"
        >
            <template #default="popupBind">
                <div class="tooltip-content" :class="{ multiline }">
                    <slot name="tooltip" v-bind="popupBind" />
                </div>
            </template>
        </Popup>
    </span>
</template>

<script>
import Popup from '@/components/utility/popup';

export default {
    name: 'Tooltip',
    components: { Popup },
    inheritAttrs: false,
    props: {
        position: { type: String, default: 'bottom' },
        align: { type: String, default: 'center' },
        
        hover: { type: Boolean, default: true },
        click: { type: Boolean, default: true },
        disabled: { type: Boolean, default: false },
        presentation: { type: Boolean, default: true },
        teleport: { type: [Boolean, String], default: false },
        delay: { type: Number, default: 500 },

        dark: { type: Boolean, default: false },
        translucent: { type: Boolean, default: false },
        multiline: { type: Boolean, default: false },
        right: { type: Boolean, default: false },
        left: { type: Boolean, default: false },
        startOpen: { type: Boolean, default: false },
        alwaysOpen: { type: Boolean, default: false },
        preventOutsideClick: { type: Boolean, default: false }
    },
    emits: ['click', 'hover', 'open', 'close', 'toggle'],
    slots: ['default', 'tooltip'],
    data () {
        return {
            timer: null,
            isOpen: false,
            isHovering: false,
            isRecentlyHovered: false
        };
    },
    watch: {
        isOpen(to) {
            this.$emit(to ? 'open' : 'close');
            if (to) {
                document.addEventListener('click', this.onOutsideClick);
            } else {
                document.removeEventListener('click', this.onOutsideClick);
            }
        },
        isHovering(to) {
            if (!to) this.onHoverAfter();
        },
        disabled(to) {
            if (to) {
                this.close();
                this.$cancel(this.timer);
            }
        }
    },
    mounted() {
        if (this.startOpen) this.open();
    },
    methods: {
        onOutsideClick(event) {
            if (this.preventOutsideClick || this.disabled) return;
            if (!this.$el.contains(event.target)) this.close();
        },
        onClick(event) {
            this.$emit('click', event);
            if (!this.click || this.disabled) return;
            if (this.isHovering) {
                this.onHoverEnd();
            } else {
                this.isOpen = !this.isOpen;
            }
        },
        onHoverStart() {
            if (this.disabled) return;
            if (this.hover && !this.isRecentlyHovered) {
                this.timer = this.$timeout(() => this.isHovering = true, this.delay);
            }
            this.$emit('hover', true);
        },
        onHoverEnd() {
            if (this.hover) this.isHovering = false;
            this.$cancel(this.timer);
            this.$emit('hover', false);
        },
        onHoverAfter() {
            this.isRecentlyHovered = true;
            this.$timeout(() => this.isRecentlyHovered = false, 500);
        },
        open() {
            if (this.isOpen || this.isHovering) return;
            this.isOpen = true;
        },
        close() {
            if (!(this.isOpen || this.isHovering)) return;
            this.isOpen = false;
            this.onHoverEnd();
        }
    }
};
</script>

<style lang="less">

.tooltip {
    position: relative;
    display: inline-flex;
    &.m-left  { margin-left: 8px; }
    &.m-right { margin-right: 8px; }
}

.popup.tooltip-popup {
    filter: var(--shadow-lg-filter);
    .popup-inner {
        border-radius: 8px;
        padding: 8px 16px;
        .tooltip-content {
            display: inline-flex;
            cursor: default;
            user-select: none;
            text-transform: none;
            word-break: break-word;
            text-align: center;
            font-weight: normal;
            > * {
                position: relative;
                z-index: 2;
                display: inline-block;
                white-space: nowrap;
                margin: 0;
            }
            &.multiline {
                width: 160px;
                > * { white-space: normal; }
            }
        }
    }
    &.top { margin-bottom: 4px; }
    &.bottom { margin-top: 4px; }
    &.left { margin-right: 4px; }
    &.right { margin-left: 4px; }
}
</style>
